<script>
  import { createEventDispatcher } from 'svelte';
  export let sections = [];
  export let headings = [];
  export let readonly = false;
  export let matchingAvailable = true;
  const dispatch = createEventDispatcher();
  const titleIdeas = ['Namų darbai', 'Pamokos tema', 'Spragos', 'Pažanga', 'Kas pavyko', 'Kitos pamokos planas', 'Mokinio įsitraukimas'];
  let focusedTitleId = null;
  let titleFields = {}, commentFields = {};

  function chooseTitle(index, title) {
    update(index, 'title', title);
    focusedTitleId = null;
    commentFields[sections[index].id]?.focus();
  }

  function dismissIdeas(event, sectionId) {
    if (event.key !== 'Escape' || focusedTitleId !== sectionId) return;
    event.preventDefault();
    event.stopPropagation();
    titleFields[sectionId]?.focus();
    focusedTitleId = null;
  }

  function change(next, nextHeadings) {
    dispatch('change', { sections: next, ...(nextHeadings === undefined ? {} : { headings: nextHeadings }) });
  }
  function update(index, field, value) {
    const row = sections[index];
    const next = sections.map((item, i) => i === index ? { ...item, [field]: value } : item);
    const recurring = field === 'title' && headings.some(item => item.id === row.id);
    change(next, recurring ? headings.map(item => item.id === row.id ? { ...item, title: value } : item) : undefined);
  }
  function toggle(row, checked) {
    change(sections, checked ? [...headings, { id: row.id, title: row.title }] : headings.filter(item => item.id !== row.id));
  }
  function remove(row) {
    change(sections.filter(item => item.id !== row.id), headings.some(item => item.id === row.id) ? headings.filter(item => item.id !== row.id) : undefined);
  }
  function move(index, offset) {
    const next = [...sections];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    const ids = new Set(next.map(row => row.id));
    const ordered = next.filter(row => headings.some(heading => heading.id === row.id)).map(row => headings.find(heading => heading.id === row.id));
    const newHeadings = [...ordered, ...headings.filter(row => !ids.has(row.id))];
    change(next, JSON.stringify(newHeadings) === JSON.stringify(headings) ? undefined : newHeadings);
  }
</script>

<div class="note-sections">
  {#each sections as section, index (section.id)}
    <section class="note-section" aria-label={`Skyrius ${index + 1}`}>
      {#if readonly}
        <h4>{section.title || 'Be pavadinimo'}</h4>
        <p class="note-text">{section.text || 'Komentaro nėra'}</p>
      {:else}
        <div class="title-editor" on:focusin={() => focusedTitleId = section.id}
          on:focusout={event => { if (!event.currentTarget.contains(event.relatedTarget)) focusedTitleId = null; }}>
          <label>Skyriaus pavadinimas
            <input maxlength="200" value={section.title} bind:this={titleFields[section.id]}
              on:keydown={event => dismissIdeas(event, section.id)}
              on:input={event => update(index, 'title', event.target.value)} />
          </label>
          {#if focusedTitleId === section.id}
            <div class="title-ideas" role="group" aria-label="Siūlomi skyrių pavadinimai">
              <p>Idėjos skyriaus pavadinimui</p>
              <div class="idea-options">
                {#each titleIdeas as idea}
                  <button type="button" on:keydown={event => dismissIdeas(event, section.id)} on:click={() => chooseTitle(index, idea)}>{idea}</button>
                {/each}
              </div>
            </div>
          {/if}
        </div>
        <label>Komentaras
          <textarea rows="4" value={section.text} bind:this={commentFields[section.id]} on:input={event => update(index, 'text', event.target.value)}></textarea>
        </label>
        <label class="recurring"><input type="checkbox" checked={headings.some(row => row.id === section.id)} disabled={!matchingAvailable}
          on:change={event => toggle(section, event.target.checked)} /> Kartoti naujuose užrašuose</label>
        <div class="section-actions">
          <button type="button" disabled={index === 0} aria-label={`Perkelti skyrių ${index + 1} aukštyn`} on:click={() => move(index, -1)}>↑</button>
          <button type="button" disabled={index === sections.length - 1} aria-label={`Perkelti skyrių ${index + 1} žemyn`} on:click={() => move(index, 1)}>↓</button>
          <button type="button" on:click={() => remove(section)}>Pašalinti skyrių</button>
        </div>
      {/if}
    </section>
  {/each}
  {#if !readonly}
    <button type="button" disabled={sections.length >= 100} on:click={() => change([...sections, { id: crypto.randomUUID(), title: '', text: '' }])}>Pridėti skyrių</button>
  {:else if sections.length === 0}
    <p>Skyrių nėra.</p>
  {/if}
</div>

<style>
  .note-section { padding: 16px 0; border-bottom: 1px solid #dedbcf; }
  .title-editor { position: relative; margin-bottom: 12px; }
  .title-editor > label { margin-bottom: 0; }
  .title-ideas { position: absolute; z-index: 5; top: calc(100% + 4px); left: 0; right: 0; padding: 12px; background: #faf9f3; border: 1px solid #a8a99d; border-radius: 6px; box-shadow: 0 4px 12px #0002; }
  .title-ideas p { margin: 0 0 8px; font-size: 13px; color: #4b5a37; }
  .idea-options { display: flex; flex-wrap: wrap; gap: 6px; }
  .idea-options button { padding: 7px 10px; min-height: 36px; font-size: 13px; text-align: left; }
  .idea-options button:hover, .idea-options button:focus-visible { background: #dfe8d2; border-color: #50633c; }
  label { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; font-size: 14px; font-weight: 600; }
  input:not([type=checkbox]), textarea { box-sizing: border-box; width: 100%; background: #fff; color: #24251f; border: 1px solid #a8a99d; border-radius: 5px; padding: 10px; font: inherit; }
  textarea { resize: vertical; min-height: 100px; font-weight: 400; }
  .recurring { flex-direction: row; align-items: center; font-weight: 400; }
  .section-actions { display: flex; gap: 8px; flex-wrap: wrap; }
  button { padding: 9px 12px; min-height: 40px; border: 1px solid #989b8a; background: #f4f3ea; color: #24251f; border-radius: 5px; cursor: pointer; }
  button:disabled { opacity: .45; cursor: default; }
  .note-sections > button { margin-top: 16px; }
  .note-text { white-space: pre-wrap; overflow-wrap: anywhere; line-height: 1.6; }
  h4 { margin: 0; overflow-wrap: anywhere; }
</style>
